import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { Mail } from 'lucide-react';
import {
  SiBluesky,
  SiFacebook,
  SiInstagram,
  SiTelegram,
  SiThreads,
  SiWhatsapp,
  SiX,
  SiYoutube,
} from 'react-icons/si';
import { Link } from '@/i18n/navigation';
import { LanguageSwitcher } from './language-switcher';
import { Logo } from '@/components/logo';
import { docsUrl } from '@/lib/links';
import { TypographyH3, TypographyP } from '@/components/ui/typography';

/** Footer link columns, driven from i18n. */
const COLUMNS = [
  {
    titleKey: 'product.title',
    links: [
      { key: 'product.create', href: '/products/create' },
      { key: 'product.sell', href: '/products/sell' },
    ],
  },
  {
    titleKey: 'company.title',
    links: [
      { key: 'company.about', href: '/about' },
      { key: 'company.contact', href: '/contact' },
    ],
  },
  {
    titleKey: 'resources.title',
    links: [
      { key: 'resources.blog', href: '/blog' },
      { key: 'resources.docs', href: docsUrl, external: true },
    ],
  },
  {
    titleKey: 'legal.title',
    links: [
      { key: 'legal.privacy', href: '/privacy' },
      { key: 'legal.terms', href: '/terms' },
      { key: 'legal.dataDeletion', href: '/data-deletion' },
    ],
  },
] as const;

/** Channels Kasbly operates on — link to the contact page. */
const SOCIALS = [
  { label: 'WhatsApp', icon: SiWhatsapp, href: 'https://wa.me/12202443441', external: true },
  {
    label: 'Instagram',
    icon: SiInstagram,
    href: 'https://www.instagram.com/kasblycom/',
    external: true,
  },
  {
    label: 'Facebook',
    icon: SiFacebook,
    href: 'https://www.facebook.com/profile.php?id=61584249137252',
    external: true,
  },
  { label: 'Telegram', icon: SiTelegram, href: 'https://t.me/kasblycombot', external: true },
  { label: 'X', icon: SiX, href: 'https://x.com/kasbly_ai', external: true },
  {
    label: 'Threads',
    icon: SiThreads,
    href: 'https://www.threads.com/@kasblycom',
    external: true,
  },
  {
    label: 'Bluesky',
    icon: SiBluesky,
    href: 'https://bsky.app/profile/kasbly.bsky.social',
    external: true,
  },
  { label: 'YouTube', icon: SiYoutube, href: 'https://www.youtube.com/@kasbly', external: true },
  { label: 'Email', icon: Mail },
] as const;

export function Footer() {
  const t = useTranslations('footer');

  return (
    <footer className="relative bg-primary text-primary-foreground">
      {/* Top-edge wave: palm green rises into the page on both sides and
          dips through the center. Full-bleed, sits just above the footer so
          its flat bottom edge meets the green band seamlessly. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 -translate-y-full select-none"
      >
        <svg
          viewBox="0 0 1440 100"
          preserveAspectRatio="none"
          fill="currentColor"
          className="block h-12 w-full text-primary sm:h-20 lg:h-24"
        >
          <path d="M0,0 C360,6 360,82 720,82 C1080,82 1080,6 1440,0 L1440,100 L0,100 Z" />
        </svg>
      </div>

      <div className="mx-auto grid max-w-6xl grid-cols-2 gap-y-10 gap-x-8 px-4 py-12 sm:px-6 sm:py-16 lg:grid-cols-[1.4fr_repeat(4,1fr)]">
        {/* Brand column */}
        <div className="col-span-2 lg:col-span-1">
          <Link href="/" className="group inline-flex">
            {/* Same locale-aware lockup as the nav, in the cream+gold light
                variant so it reads directly on the green band — no badge. */}
            <Logo variant="light" className="h-9 transition-brand group-hover:opacity-85" />
          </Link>

          <TypographyP className="mt-4 max-w-xs text-small text-primary-foreground/70 [&:not(:first-child)]:mt-4">
            {t('tagline')}
          </TypographyP>

          {/* Social channels */}
          <div className="mt-6 flex items-center gap-4">
            {SOCIALS.map((social) => {
              const { label, icon: Icon } = social;
              const className =
                'text-primary-foreground/70 transition-brand hover:text-brand-terracotta';
              // Socials with an explicit external href (e.g. the Telegram bot)
              // are real outbound links; the rest route to the contact page.
              if ('external' in social && social.external && 'href' in social && social.href) {
                return (
                  <a
                    key={label}
                    href={social.href}
                    aria-label={label}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={className}
                  >
                    <Icon className="h-5 w-5" aria-hidden />
                  </a>
                );
              }
              return (
                <Link key={label} href="/contact" aria-label={label} className={className}>
                  <Icon className="h-5 w-5" aria-hidden />
                </Link>
              );
            })}
          </div>
        </div>

        {/* Link columns */}
        {COLUMNS.map((col) => (
          <div key={col.titleKey}>
            <TypographyH3 className="text-small font-semibold text-primary-foreground">
              {t(col.titleKey)}
            </TypographyH3>
            {/* Gold underline accent */}
            <span className="mt-2 block h-px w-8 bg-brand-gold/70" aria-hidden />
            <ul className="mt-4 space-y-3 text-small text-primary-foreground/70">
              {col.links.map((link) => (
                <li key={link.key}>
                  {'external' in link && link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="transition-brand hover:text-primary-foreground"
                    >
                      {t(link.key)}
                    </a>
                  ) : (
                    <Link
                      href={link.href}
                      className="transition-brand hover:text-primary-foreground"
                    >
                      {t(link.key)}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Copyright + language switcher */}
      <div className="border-t border-primary-foreground/20">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-5 sm:px-6">
          <TypographyP className="text-small text-primary-foreground/70">
            {t('copyright')}
          </TypographyP>
          <div className="flex items-center gap-3">
            <span className="h-4 w-px bg-primary-foreground/20" aria-hidden />
            <LanguageSwitcher className="text-primary-foreground/70 hover:text-primary-foreground" />
          </div>
        </div>
      </div>

      {/* Braided line motif — full-bleed, closes the footer */}
      <Image
        src="/footer-lines.svg"
        alt=""
        aria-hidden
        width={3517}
        height={159}
        className="block h-12 w-full object-cover object-bottom opacity-70 sm:h-16 lg:h-auto"
      />
    </footer>
  );
}
